<script setup lang="ts">
import { reactive, ref, computed, onMounted } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import router from "../../router";
import axios from "axios";
import { ElMessage } from "element-plus";
import { useAuthStore } from "../../store/authStore";
import { isCompanyAdminFromToken } from "../../utils/authRole";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

const ensureAdminAccess = () => {
  if (!isCompanyAdminFromToken(localStorage.getItem("_panda"))) {
    router.replace("/request");
    return false;
  }
  return true;
};

interface RuleForm {
  name: string;
  email: string;
  phone: string;
  user_type: string;
  // user_status: string;
}

// Interface for edited user to include user_id
interface EditedUser {
  user_name: string;
  user_phone: string;
  user_email: string;
  user_type: string;
  user_status: string;
  user_id?: number | string;
}

// Helper function to normalize user_type to canonical string value
const normalizeUserType = (userType: string | number | undefined): string => {
  if (!userType) return "1";
  if (userType === "Admin" || userType === "2" || userType === 2) return "2";
  if (userType === "User" || userType === "1" || userType === 1) return "1";
  return String(userType);
};

// Helper function to check if user type is Admin
const isAdminType = (userType: string | number | undefined): boolean => {
  return normalizeUserType(userType) === "2";
};

const formSize = ref<"default" | "small" | "large">("default");
const formError = ref("");
const results = ref();
const loading = ref(false);
const ruleFormRef = ref<FormInstance>();
const ruleForm = reactive<RuleForm>({
  name: "",
  phone: "",
  email: "",
  user_type: "",
  // user_status: "",
});

/** Company user list for sole-admin check (same shape as Users.vue after mapping). */
const companyUsers = ref<{ user_id: number; user_type: string | number }[]>([]);

const loadCompanyUsers = async () => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_ONBOARDING_SERVICE}getusers?token=${store.getToken}`,
    );
    if (response.status === 200 && Array.isArray(response.data)) {
      companyUsers.value = response.data.map(
        (el: { user_type: number; user_id: number }) => ({
          ...el,
          user_type: el.user_type == 1 ? "Normal" : "Admin",
        }),
      );
    }
  } catch {
    companyUsers.value = [];
  }
};

onMounted(() => {
  if (!ensureAdminAccess()) return;
  void loadCompanyUsers();
});

const rules = reactive<FormRules<RuleForm>>({
  name: [
    {
      required: true,
      message: "Please enter the name of the user",
      trigger: "blur",
    },
  ],
  phone: [
    {
      required: true,
      message: "Please enter the user phone number",
      trigger: "blur",
    },
  ],
  email: [
    {
      required: true,
      message: "Please enter the user email address",
      trigger: "blur",
    },
  ],
  user_type: [
    { required: true, message: "Please enter the user type", trigger: "blur" },
  ],
  // user_status: [
  //   {
  //     required: true,
  //     message: "Please select the user status",
  //     trigger: "blur",
  //   },
  // ],
});

// Current logged-in user's admin rights — JWT-based (single source of truth)
const isCurrentUserAdmin = computed(() =>
  isCompanyAdminFromToken(localStorage.getItem("_panda")),
);

// Detect if the role is being changed
const isRoleChange = computed(() => {
  const currentEditedUser = (store.getCurrentEditedUser || {}) as EditedUser;
  const normalizedFormType = normalizeUserType(ruleForm.user_type);
  const normalizedEditedType = normalizeUserType(currentEditedUser.user_type);
  return normalizedFormType !== normalizedEditedType;
});

// Prevent modifying your own role
const isSelfRoleModification = computed(() => {
  const currentUser = store.getUserData || {};
  const currentEditedUser = (store.getCurrentEditedUser || {}) as EditedUser;
  const currentUserId = currentUser.user_id;
  const editedUserId = currentEditedUser.user_id;
  return (
    currentUserId &&
    editedUserId &&
    String(currentUserId) === String(editedUserId) &&
    isRoleChange.value
  );
});

// Sole-admin guard using the loaded company user list
const isOnlyAdmin = computed(() => {
  const adminUsers = companyUsers.value.filter(
    (u) => u.user_type === "Admin" || u.user_type === 2 || u.user_type === "2",
  );
  return (
    ruleForm.user_type !== "2" &&
    store.getCurrentEditedUser.user_type == "Admin" &&
    adminUsers.length === 1 &&
    store.getCurrentEditedUser.user_id === adminUsers[0].user_id
  );
});

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate((valid, fields) => {
    if (valid) {
      // Role restriction: only Admin users can modify roles
      if (isRoleChange.value && !isCurrentUserAdmin.value) {
        formError.value = "Only Admin users can modify user roles.";
        return;
      }

      // Prevent modifying your own role
      if (isSelfRoleModification.value) {
        formError.value = "You cannot modify your own role.";
        return;
      }

      // Prevent demoting/deleting the only admin
      if (isOnlyAdmin.value) {
        formError.value =
          "You cannot demote or delete the only admin user. Please assign another admin first.";
        return;
      }

      loading.value = true;
      postData();
    } else {
      formError.value = "Something went wrong please try again.";
      console.log("error submit!", fields);
      console.log(ruleForm);
    }
  });
};

const postData = async () => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_ONBOARDING_SERVICE}updateuser?token=${store.getToken}`,
      {
        email: ruleForm.email,
        name: ruleForm.name,
        phone: ruleForm.phone,
        user_type: Number(ruleForm.user_type),
      },
    );

    // Handle the API response here
    loading.value = false;

    if (response.status == 200) {
      if (response.data.status) {
        // Check if current user's own role was changed
        const currentUser = store.getUserData || {};
        const currentEditedUser = (store.getCurrentEditedUser || {}) as EditedUser;
        const isCurrentUser = currentUser.user_id && currentEditedUser.user_id && 
                             String(currentUser.user_id) === String(currentEditedUser.user_id);
        const wasRoleChange = isRoleChange.value;
        
        if (isCurrentUser && wasRoleChange) {
          ElMessage({
            message: "Your role has been updated. You will be logged out to apply new permissions.",
            type: "warning",
            duration: 3000,
          });
          // Force logout after role change for current user
          setTimeout(() => {
            logwat();
          }, 2000);
          return; // Don't navigate to users page
        } else {
          ElMessage({
            message: "The user record has been saved",
            type: "success",
          });
        }
        router.push("/account/team");
      } else {
        ElMessage({
          message:
            "Your session has expired. Kindy login to continue enjoying our services",
          type: "error",
        });
        logwat();
      }
    } else {
      formError.value = "User update failed, please retry.";
    }

    console.log("Response:", response.data);
  } catch (error) {
    // Handle any errors
    loading.value = false;
    formError.value = "Something went wrong please try again.";
    console.error("Error:", error);
  }
};

const logwat = () => {
  store.setAuthStatus(false);
  store.$reset();
  localStorage.removeItem("_panda");
  router.push("/login");
};

const navigateBack = () => {
  router.push("/account/team");
};

const initializeForm = () => {
  ruleForm.email = store.getCurrentEditedUser.user_email;
  ruleForm.phone = store.getCurrentEditedUser.user_phone;
  ruleForm.name = store.getCurrentEditedUser.user_name;
  const ut = store.getCurrentEditedUser.user_type;
  ruleForm.user_type = ut === "Admin" || ut === 2 || ut === "2" ? "2" : "1";
};
initializeForm();
</script>

<template>
  <div class="ac-card" v-loading="loading">
    <div class="ac-panel-head">
      <div>
        <h2 class="ac-panel-title">Edit a user</h2>
        <p class="ac-panel-desc">Update this user's profile and access role</p>
      </div>
    </div>

    <div class="ac-card-body">
      <div class="ac-error" v-if="formError">{{ formError }}</div>

      <el-form
        ref="ruleFormRef"
        :model="ruleForm"
        :rules="rules"
        label-width="0px"
        :size="formSize"
        status-icon
      >
        <div class="ac-section-label">Profile</div>
        <div class="ac-grid">
          <div class="ac-field">
            <label class="ac-label" for="eu-name"
              >Name <span class="ac-req">*</span></label
            >
            <el-form-item prop="name" class="ac-fi">
              <el-input
                id="eu-name"
                v-model="ruleForm.name"
                placeholder="Enter the name of the user"
                size="large"
              />
            </el-form-item>
          </div>

          <div class="ac-field">
            <label class="ac-label" for="eu-email"
              >Email Address <span class="ac-req">*</span></label
            >
            <el-form-item prop="email" class="ac-fi">
              <el-input
                id="eu-email"
                v-model="ruleForm.email"
                placeholder="Enter the user email"
                size="large"
              />
            </el-form-item>
          </div>

          <div class="ac-field">
            <label class="ac-label" for="eu-phone"
              >Phone Number <span class="ac-req">*</span></label
            >
            <el-form-item prop="phone" class="ac-fi">
              <el-tooltip
                content="Only the users can change their phone numbers"
                placement="top"
              >
                <el-input
                  id="eu-phone"
                  v-model="ruleForm.phone"
                  placeholder="Enter the user phone number"
                  size="large"
                  disabled
                />
              </el-tooltip>
            </el-form-item>
          </div>
        </div>

        <div class="ac-section-label">Access</div>
        <div class="ac-grid ac-grid--one">
          <div class="ac-field">
            <label class="ac-label"
              >Role <span class="ac-req">*</span></label
            >
            <el-form-item prop="user_type" class="ac-fi">
              <el-select
                v-model="ruleForm.user_type"
                placeholder="please select the user type"
                size="large"
                :disabled="!isCurrentUserAdmin"
              >
                <el-option label="Admin" value="2" />
                <el-option label="Normal" value="1" />
              </el-select>
            </el-form-item>
          </div>
        </div>

        <div class="ac-actions">
          <button class="ac-btn ac-btn--ghost" @click.prevent="navigateBack()">
            ← Back to Users
          </button>
          <button
            class="ac-btn"
            @click.prevent="submitForm(ruleFormRef)"
            :disabled="loading"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
              />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            {{ loading ? "Updating..." : "Update User" }}
          </button>
        </div>
      </el-form>
    </div>
  </div>
</template>
